// Russian noun-case homework sets — hand-authored and verified, transcribed from
// real worksheets. Same format every time: a sentence with ONE noun given in its
// dictionary form, which the learner must put into the correct case.
//
// Each item is in the shape `shapeCaseItem` (SpecialModeView) consumes:
//   baseNoun          the dictionary form shown in brackets
//   gender            'm' | 'f' | 'n'   (of the noun; used by the reference board)
//   number            'singular' | 'plural'
//   case              nominative | accusative | genitive | dative | instrumental | prepositional
//   answer            the correctly inflected form
//   sentenceTemplate  the sentence with ⟦N⟧ where the answer goes
//   english           translation of the whole sentence
//   why               ONE line naming the trigger (shown after the case is settled)
//   endingHint        how to FORM the ending — never the answer itself
//
// The drill never shows `answer` until the learner asks; `why` and `endingHint`
// are the graded help in between.

const I = (baseNoun, gender, number, kase, answer, sentenceTemplate, english, why, endingHint) =>
  ({ baseNoun, gender, number, case: kase, answer, sentenceTemplate, english, why, endingHint })

export const RU_CASE_SETS = [
  {
    id: 'hw-mixed-1',
    title: 'Homework 1 — mixed cases',
    note: 'Ten sentences across the dative, accusative, genitive, instrumental and prepositional.',
    items: [
      I('врач', 'm', 'singular', 'dative', 'врачу',
        'Вчера мы ходили к ⟦N⟧.', 'Yesterday we went to the doctor.',
        '«к» always takes the dative — it marks the person you go towards.',
        'A hard masculine noun takes -у in the dative singular.'),
      I('кошка', 'f', 'singular', 'accusative', 'кошку',
        '⟦N⟧ зовут Мурка.', 'The cat is called Murka.',
        '«зовут» (they call) takes a direct object, so the cat is accusative.',
        'A feminine noun in -а becomes -у in the accusative singular.'),
      I('блюдо', 'n', 'singular', 'accusative', 'блюдо',
        'Какое ⟦N⟧ вы предпочитаете?', 'Which dish do you prefer?',
        '«предпочитаете» takes a direct object → accusative.',
        'Inanimate neuter nouns look the same in the accusative as in the nominative.'),
      I('дети', 'm', 'plural', 'dative', 'детям',
        '⟦N⟧ нравятся конфеты.', 'Children like sweets.',
        'With «нравиться» the person who likes something goes in the dative.',
        'The dative plural ending is -ам / -ям.'),
      I('рыба', 'f', 'singular', 'genitive', 'рыбы',
        'Сколько ⟦N⟧ ты купил?', 'How much fish did you buy?',
        'Quantity words like «сколько» are followed by the genitive.',
        'A feminine noun in -а takes -ы in the genitive singular.'),
      I('солнце', 'n', 'singular', 'genitive', 'солнца',
        'Зимой очень мало ⟦N⟧.', 'In winter there is very little sun.',
        '«мало» (little / few) is a quantity word → genitive.',
        'A neuter noun in -е takes -а in the genitive singular.'),
      I('друзья', 'm', 'plural', 'instrumental', 'друзьями',
        'Сегодня мы идём в кино с ⟦N⟧.', 'Today we are going to the cinema with friends.',
        '«с» meaning "with" takes the instrumental.',
        'The instrumental plural ending is -ами / -ями.'),
      I('аптека', 'f', 'singular', 'instrumental', 'аптекой',
        'Рядом с ⟦N⟧ есть большой парк.', 'Next to the pharmacy there is a big park.',
        '«рядом с» (next to) contains «с» → instrumental.',
        'A feminine noun in -а takes -ой in the instrumental singular.'),
      I('газеты', 'f', 'plural', 'prepositional', 'газетах',
        'В ⟦N⟧ пишут о политике.', 'They write about politics in the newspapers.',
        '«в» meaning "in / where?" is location → prepositional.',
        'The prepositional plural ending is -ах / -ях.'),
      I('спорт', 'm', 'singular', 'instrumental', 'спортом',
        'Как часто ты занимаешься ⟦N⟧?', 'How often do you do sport?',
        '«заниматься» always takes the instrumental.',
        'A hard masculine noun takes -ом in the instrumental singular.'),
    ],
  },
  {
    id: 'hw-mixed-2',
    title: 'Homework 2 — mixed cases',
    note: 'Quantities, days, transport and the bare instrumental of time.',
    items: [
      I('пиво', 'n', 'singular', 'genitive', 'пива',
        'Саша купил один литр ⟦N⟧.', 'Sasha bought one litre of beer.',
        'A measure word like «литр» is followed by the genitive — "a litre OF beer".',
        'A neuter noun in -о takes -а in the genitive singular.'),
      I('суббота', 'f', 'singular', 'accusative', 'субботу',
        'В ⟦N⟧ мы часто идём ужинать в ресторан.', 'On Saturday we often go out for dinner.',
        'For days of the week, «в» takes the accusative, not the prepositional.',
        'A feminine noun in -а becomes -у in the accusative singular.'),
      I('телефон', 'm', 'singular', 'dative', 'телефону',
        'Ты слишком долго говоришь по ⟦N⟧!', 'You talk on the phone for too long!',
        '«по» takes the dative.',
        'A hard masculine noun takes -у in the dative singular.'),
      I('идеи', 'f', 'plural', 'genitive', 'идей',
        'У меня есть много ⟦N⟧.', 'I have a lot of ideas.',
        '«много» is a quantity word → genitive plural.',
        'Feminine nouns take a ZERO ending in the genitive plural — no -ов here; the final -я is replaced by -й.'),
      I('машина', 'f', 'singular', 'prepositional', 'машине',
        'Ты любишь ездить на ⟦N⟧?', 'Do you like travelling by car?',
        '«на» with a means of transport answers "on what?" → prepositional.',
        'A feminine noun in -а takes -е in the prepositional singular.'),
      I('интернет', 'm', 'singular', 'prepositional', 'интернете',
        'Студенты читают новости в ⟦N⟧.', 'Students read the news on the internet.',
        '«в» meaning "in / where?" is location → prepositional.',
        'A hard masculine noun takes -е in the prepositional singular.'),
      I('новости', 'f', 'plural', 'prepositional', 'новостях',
        'В ⟦N⟧ часто говорят об экологии.', 'They often talk about ecology in the news.',
        '«в» meaning "in / where?" is location → prepositional.',
        'The prepositional plural ending is -ах / -ях.'),
      I('мир', 'm', 'singular', 'dative', 'миру',
        'Мой дядя сейчас путешествует по ⟦N⟧.', 'My uncle is travelling around the world at the moment.',
        '«по» meaning "around / along" takes the dative.',
        'A hard masculine noun takes -у in the dative singular.'),
      I('час', 'm', 'plural', 'genitive', 'часов',
        'Я ждал тебя несколько ⟦N⟧.', 'I waited for you for several hours.',
        '«несколько» is a quantity word → genitive plural.',
        'Hard masculine nouns take -ов in the genitive plural.'),
      I('лето', 'n', 'singular', 'instrumental', 'летом',
        '⟦N⟧ всегда хорошая погода.', 'In summer the weather is always good.',
        'Seasons and times of day use the bare instrumental to mean "in / during".',
        'A neuter noun in -о takes -ом in the instrumental — and no preposition is needed.'),
    ],
  },
]

export default RU_CASE_SETS
